import React, { Component } from "react";
import Constants from "Helpers/Constants";
import axios from "axios";
import init from "Helpers/WindowToken";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";

import {
  Card,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";

import MDBox from "components/MDBox";
import MDSnackbar from "components/MDSnackbar";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

export default class SchoolEnquiry extends Component {
  state = {
    enquiries: [],
    pagination: {
      page: 1,
      totalPages: 1,
      hasNextPage: false,
      hasPrevPage: false,
      nextPage: null,
      prevPage: null,
      totalDocs: 0,
      limit: 20,
    },
    per_page: 20,
    page_no: 1,
    notiColor: "success",
    notification: false,
    message: "",
    loading: false,

    // reply modal state
    replyModalOpen: false,
    activeEnquiry: null,
    emailSubject: "",
    emailMessage: "",
    sendingEmail: false,
  };

  componentDidMount() {
    this.getAllEnquiries(this.state.page_no, this.state.per_page);
  }

  // ------------------- API & table helpers -------------------
  getAllEnquiries = (page = 1, limit = 20) => {
    this.setState({ loading: true });

    if (init() !== "success") {
      this.setState({ loading: false });
      return;
    }

    axios
      .get(`${Constants.getUrls.getSchoolEnquiry}?page=${page}&limit=${limit}`)
      .then((resp) => {
        const meta = resp.data?.meta || {};
        const docs = resp.data?.data || [];

        this.setState({
          enquiries: Array.isArray(docs) ? docs : [],
          pagination: {
            page: meta.page || page,
            totalPages: meta.totalPages || 1,
            hasNextPage: meta.hasNextPage || false,
            hasPrevPage: meta.hasPrevPage || false,
            nextPage: meta.nextPage || null,
            prevPage: meta.prevPage || null,
            totalDocs: meta.totalDocs || docs.length,
            limit: meta.limit || limit,
          },
          page_no: meta.page || page,
          loading: false,
        });
      })
      .catch((err) => {
        this.setState({
          notiColor: "warning",
          message: err?.response?.data?.message || "Failed to load enquiries",
          notification: true,
          loading: false,
        });
        setTimeout(() => {
          this.setState({
            notification: false,
            message: "",
            notiColor: "success",
          });
        }, 3500);
      });
  };

  handleEntriesPerPageChange = (newLimit) => {
    this.setState(
      {
        per_page: newLimit,
      },
      () => {
        this.getAllEnquiries(1, newLimit); // reset to first page
      },
    );
  };

  handlePageChange = (newPage) => {
    this.getAllEnquiries(newPage, this.state.per_page);
  };

  handleDelete = (id) => {
    if (!window.confirm("Are you sure you want to delete this enquiry?"))
      return;

    if (init() !== "success") {
      return window.location.assign("/signin");
    }

    axios
      .delete(Constants.deleteUrls.deleteSchoolEnquiry + id)
      .then((resp) => {
        this.setState(
          {
            notification: true,
            notiColor: "success",
            message: resp.data?.message || "Enquiry deleted",
          },
          () => {
            this.getAllEnquiries(this.state.page_no, this.state.per_page);
          },
        );
        setTimeout(() => {
          this.setState({ notification: false, message: "" });
        }, 2500);
      })
      .catch((err) => {
        this.setState({
          notification: true,
          notiColor: "warning",
          message: err?.response?.data?.message || "Failed to delete enquiry",
        });
        setTimeout(() => {
          this.setState({ notification: false, message: "" });
        }, 3500);
      });
  };

  // ---------- Reply modal handlers ----------
  openReplyModal = (enquiry) => {
    const defaultSubject = `Response to your enquiry`;
    const defaultMessage = `Hi ${
      enquiry.contact_person_name || enquiry.name || ""
    },\n\nThank you for contacting us. We will get back to you shortly.\n\nRegards,`;

    this.setState({
      replyModalOpen: true,
      activeEnquiry: enquiry,
      emailSubject: defaultSubject,
      emailMessage: defaultMessage,
    });
  };

  closeReplyModal = () => {
    this.setState({
      replyModalOpen: false,
      activeEnquiry: null,
      emailSubject: "",
      emailMessage: "",
      sendingEmail: false,
    });
  };

  handleEmailInput = (field, value) => {
    this.setState({ [field]: value });
  };

  sendEmailToEnquiry = async () => {
    const { activeEnquiry, emailSubject, emailMessage } = this.state;
    if (!activeEnquiry) return;

    if (!emailSubject || !emailMessage) {
      this.setState({
        notification: true,
        notiColor: "warning",
        message: "Subject and message are required",
      });
      setTimeout(
        () => this.setState({ notification: false, message: "" }),
        3000,
      );
      return;
    }

    if (init() !== "success") {
      return window.location.assign("/signin");
    }

    this.setState({ sendingEmail: true });

    try {
      const postUrlTemplate =
        Constants.postUrls?.sendMailSchool || "/admin/enquiries/:id/send-email";
      const url = postUrlTemplate.includes(":id")
        ? postUrlTemplate.replace(":id", activeEnquiry._id)
        : `${postUrlTemplate}/${activeEnquiry._id}`;

      const payload = {
        subject: emailSubject,
        message: emailMessage,
      };

      const resp = await axios.post(url, payload);

      if (resp.data?.status === "success") {
        this.setState({
          notification: true,
          notiColor: "success",
          message: resp.data?.message || "Email sent successfully",
        });
        this.closeReplyModal();
        this.getAllEnquiries(this.state.page_no, this.state.per_page);
      } else {
        this.setState({
          notification: true,
          notiColor: "warning",
          message: resp.data?.message || "Failed to send email",
        });
      }
    } catch (err) {
      this.setState({
        notification: true,
        notiColor: "warning",
        message:
          err?.response?.data?.message || err.message || "Failed to send email",
      });
    } finally {
      this.setState({ sendingEmail: false });
      setTimeout(
        () => this.setState({ notification: false, message: "" }),
        3500,
      );
    }
  };

  // ---------------- Gmail compose helpers ----------------
  encodeForQuery = (str = "") => {
    const normalized = String(str).replace(/\r\n/g, "\n").replace(/\r/g, "\n");
    return encodeURIComponent(normalized);
  };

  buildGmailComposeUrl = ({
    to = "",
    subject = "",
    body = "",
    cc = "",
    bcc = "",
  }) => {
    const base = "https://mail.google.com/mail/?view=cm&tf=1";
    const params = [];

    if (to) params.push(`to=${this.encodeForQuery(to)}`);
    if (cc) params.push(`cc=${this.encodeForQuery(cc)}`);
    if (bcc) params.push(`bcc=${this.encodeForQuery(bcc)}`);
    if (subject) params.push(`su=${this.encodeForQuery(subject)}`);
    if (body) params.push(`body=${this.encodeForQuery(body)}`);

    return `${base}&${params.join("&")}`;
  };

  openGmailCompose = (enquiry) => {
    if (!enquiry) return;

    const to = enquiry.email || enquiry.contact_email || "";
    const subject = `Response to your enquiry`;
    const body = `Hi ${
      enquiry.contact_person_name || enquiry.name || ""
    },\n\nThank you for reaching out. We received your enquiry and will contact you shortly.\n\nYour message:\n${
      enquiry.message || ""
    }\n\nRegards,\nYour Team`;

    const gmailUrl = this.buildGmailComposeUrl({ to, subject, body });

    try {
      window.open(gmailUrl, "_blank", "noopener,noreferrer");
    } catch (err) {
      const mailto = `mailto:${encodeURIComponent(
        to,
      )}?subject=${this.encodeForQuery(subject)}&body=${this.encodeForQuery(
        body,
      )}`;
      window.open(mailto, "_blank", "noopener,noreferrer");
    }
  };

  // ---------------- render ----------------
  render() {
    const { enquiries, loading, pagination } = this.state;
    const {
      replyModalOpen,
      activeEnquiry,
      emailSubject,
      emailMessage,
      sendingEmail,
    } = this.state;

    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox pt={6} pb={3}>
          <Card>
            <MDBox
              mx={2}
              mt={-3}
              py={3}
              px={2}
              variant="gradient"
              bgColor="info"
              borderRadius="lg"
              coloredShadow="info"
            >
              <MDTypography variant="h6" color="white">
                School Enquiries
              </MDTypography>
            </MDBox>

            <MDBox py={3}>
              {loading ? (
                <MDBox>
                  <Grid align="center">
                    <h4>Loading...</h4>
                  </Grid>
                </MDBox>
              ) : enquiries.length > 0 ? (
                <DataTable
                  table={{
                    columns: [
                      { Header: "serial", accessor: "serial", width: "6%" },
                      { Header: "name", accessor: "name" },
                      { Header: "email", accessor: "email" },
                      { Header: "mobile", accessor: "mobile_no" },
                      { Header: "school", accessor: "school_name" },
                      { Header: "position", accessor: "position" },
                      { Header: "message", accessor: "message" },
                      { Header: "action", accessor: "action" },
                    ],
                    rows: enquiries.map((item, index) => {
                      const message = item.message || "";
                      const shortMessage =
                        message.length > 80
                          ? message.substring(0, 80) + "..."
                          : message;

                      const page = pagination.page || 1;
                      const limit =
                        pagination.limit || this.state.per_page || 20;
                      const serial = (page - 1) * limit + (index + 1);

                      return {
                        serial,
                        name: item.contact_person_name || item.name || "",
                        email: (
                          <span
                            style={{
                              color: "#1a73e8",
                              cursor: "pointer",
                              textDecoration: "underline",
                            }}
                            onClick={() => this.openGmailCompose(item)}
                          >
                            {item.email}
                          </span>
                        ),
                        mobile_no: item.mobile_no || item.phone || "",
                        school_name: item.school_name || "",
                        position: item.position || "",
                        message: shortMessage,
                        action: (
                          <div style={{ display: "flex", gap: 8 }}>
                            <MDButton
                              variant="outlined"
                              size="small"
                              color="info"
                              onClick={() => this.openReplyModal(item)}
                            >
                              Reply
                            </MDButton>
                            <MDButton
                              variant="outlined"
                              size="small"
                              color="error"
                              onClick={() => this.handleDelete(item._id)}
                            >
                              Delete
                            </MDButton>
                          </div>
                        ),
                      };
                    }),
                  }}
                  entriesPerPage={{ defaultValue: this.state.per_page }}
                  page_no={pagination.page}
                  totalPages={pagination.totalPages}
                  hasNextPage={pagination.hasNextPage}
                  hasPrevPage={pagination.hasPrevPage}
                  isnextPage={pagination.nextPage}
                  prevPage={pagination.prevPage}
                  totalDocs={pagination.totalDocs}
                  onPageChange={this.handlePageChange}
                  onEntriesPerPageChange={this.handleEntriesPerPageChange}
                />
              ) : (
                <div style={{ padding: 16 }}>No Enquiries Found!</div>
              )}
            </MDBox>

            <MDSnackbar
              color={this.state.notiColor}
              icon="notifications"
              title="Fabinov8 | School Enquiries"
              content={this.state.message}
              dateTime="Just now"
              open={this.state.notification}
            />
          </Card>
        </MDBox>

        {/* Reply Modal */}
        <Dialog
          open={replyModalOpen}
          onClose={this.closeReplyModal}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Reply to Enquiry</DialogTitle>
          <DialogContent dividers>
            <div style={{ marginBottom: 12 }}>
              <strong>To: </strong>{" "}
              {activeEnquiry
                ? activeEnquiry.email || activeEnquiry.contact_email
                : ""}
            </div>

            <TextField
              label="Subject"
              value={emailSubject}
              onChange={(e) =>
                this.handleEmailInput("emailSubject", e.target.value)
              }
              fullWidth
              margin="normal"
            />

            <TextField
              label="Message"
              value={emailMessage}
              onChange={(e) =>
                this.handleEmailInput("emailMessage", e.target.value)
              }
              fullWidth
              margin="normal"
              multiline
              minRows={6}
              placeholder="Write your message here..."
            />
          </DialogContent>

          <DialogActions style={{ padding: "16px 24px" }}>
            <MDButton
              variant="outlined"
              color="info"
              onClick={this.closeReplyModal}
            >
              Cancel
            </MDButton>
            <MDButton
              variant="contained"
              color="info"
              onClick={this.sendEmailToEnquiry}
              disabled={sendingEmail}
            >
              {sendingEmail ? "Sending..." : "Reply"}
            </MDButton>
          </DialogActions>
        </Dialog>
      </DashboardLayout>
    );
  }
}
